import { useReportStateContext } from "./useReportState";
import { useCallback, useState } from "react";

export const useColumnMenu = ({
  apiRef,
  clonedSeparatorsRef,
  truncatedColumnsRef,
  getOrderedGridColumns,
  getColumnVisibilityModel,
  setColumnNameOverrides,
  onRenameRemove,
}) => {
  const { sortModel, setSortModel } = useReportStateContext();
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [menuColumn, setMenuColumn] = useState(null);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameField, setRenameField] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const columnMenuOpen = Boolean(columnMenuAnchor);

  const openColumnMenu = useCallback(
    (params, event) => {
      event.defaultMuiPrevented = true;
      const headerElement = event.target.closest(".MuiDataGrid-columnHeader");

      clonedSeparatorsRef.current.forEach((clonedSeparator) => {
        if (clonedSeparator && clonedSeparator.parentNode) {
          clonedSeparator.remove();
        }
      });
      clonedSeparatorsRef.current.clear();

      if (truncatedColumnsRef.current.has(params.field)) {
        const draggableContainer = headerElement?.querySelector(
          ".MuiDataGrid-columnHeaderDraggableContainer"
        );
        const separator = headerElement?.querySelector(
          ".MuiDataGrid-columnSeparator"
        );
        if (draggableContainer && separator) {
          const orderedGridColumns = getOrderedGridColumns();
          const columnVisibilityModel = getColumnVisibilityModel();
          const visibleColumns = orderedGridColumns.filter(
            (col) => columnVisibilityModel[col.field] !== false
          );
          const isLastColumn =
            visibleColumns[visibleColumns.length - 1]?.field === params.field;

          const separatorClone = separator.cloneNode(true);
          Object.assign(separatorClone.style, {
            position: "absolute",
            visibility: "visible",
            opacity: "1",
            display: "flex",
          });

          if (isLastColumn) {
            separatorClone.style.left = "0";
            separatorClone.style.right = "auto";
            separatorClone.style.paddingRight = "8px";
          } else {
            separatorClone.style.right = "0";
            separatorClone.style.paddingLeft = "8px";
          }
          draggableContainer.appendChild(separatorClone);

          clonedSeparatorsRef.current.set(params.field, separatorClone);
        }
      }
      setColumnMenuAnchor(headerElement);
      setMenuColumn(params.field);
    },
    [
      clonedSeparatorsRef,
      truncatedColumnsRef,
      getOrderedGridColumns,
      getColumnVisibilityModel,
    ]
  );

  const handleColumnHeaderClick = useCallback(
    (params, event) => {
      openColumnMenu(params, event);
    },
    [openColumnMenu]
  );

  // Enter and Space on a focused header open the column menu, so it can be
  // reached without a mouse. Like a button, Enter acts on key down and Space
  // on key up, so the key is released before focus moves into the menu and
  // cannot also choose its first item. Attached to the grid root, as the grid
  // has no header key props. Keys pressed inside header content (aggregation
  // selects, buttons) and on column group headers are left alone.
  const openColumnMenuFromKey = useCallback(
    (event, key) => {
      if (event.key !== key) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const headerElement = event.target.closest?.(".MuiDataGrid-columnHeader");
      const field = headerElement?.dataset.field;
      if (!field || headerElement !== event.target) return;
      event.preventDefault();
      openColumnMenu({ field }, event);
    },
    [openColumnMenu]
  );

  const handleColumnHeaderKeyDown = useCallback(
    (event) => openColumnMenuFromKey(event, "Enter"),
    [openColumnMenuFromKey]
  );

  const handleColumnHeaderKeyUp = useCallback(
    (event) => openColumnMenuFromKey(event, " "),
    [openColumnMenuFromKey]
  );

  const handleColumnHeaderContextMenu = useCallback(
    (params, event) => {
      event.preventDefault();
      openColumnMenu(params, event);
    },
    [openColumnMenu]
  );

  const handleCloseColumnMenu = useCallback(() => {
    const currentMenuColumn = menuColumn;
    // While focus is still in the menu, give it back to the header the menu
    // was opened from. A dialog or the filter panel opened from a menu item
    // then returns to the header when it closes, as MUI dialogs and popovers
    // hand focus back to whatever had it when they opened. Focus that has
    // already left the menu (a click elsewhere) is left alone.
    if (
      columnMenuAnchor?.isConnected &&
      document.activeElement?.closest?.('[role="menu"]')
    ) {
      columnMenuAnchor.focus();
    }
    setColumnMenuAnchor(null);
    setMenuColumn(null);

    setTimeout(() => {
      if (
        currentMenuColumn &&
        truncatedColumnsRef.current.has(currentMenuColumn)
      ) {
        const clonedSeparator = clonedSeparatorsRef.current.get(
          currentMenuColumn
        );
        if (clonedSeparator && clonedSeparator.parentNode) {
          clonedSeparator.remove();
          clonedSeparatorsRef.current.delete(currentMenuColumn);
        }
      }
    }, 0);
  }, [menuColumn, columnMenuAnchor, truncatedColumnsRef, clonedSeparatorsRef]);

  const handleSortAsc = useCallback(() => {
    if (menuColumn) {
      setSortModel((prev) => [
        { field: menuColumn, sort: "asc" },
        ...prev.filter((s) => s.field !== menuColumn),
      ]);
    }
    handleCloseColumnMenu();
  }, [menuColumn, setSortModel, handleCloseColumnMenu]);
  const handleSortDesc = useCallback(() => {
    if (menuColumn) {
      setSortModel((prev) => [
        { field: menuColumn, sort: "desc" },
        ...prev.filter((s) => s.field !== menuColumn),
      ]);
    }
    handleCloseColumnMenu();
  }, [menuColumn, setSortModel, handleCloseColumnMenu]);
  const handleClearSort = useCallback(() => {
    if (menuColumn) {
      setSortModel((prev) => prev.filter((s) => s.field !== menuColumn));
    }
    handleCloseColumnMenu();
  }, [menuColumn, setSortModel, handleCloseColumnMenu]);
  const isMenuColumnSorted = Boolean(
    menuColumn && sortModel.some((sortItem) => sortItem.field === menuColumn)
  );

  const handleRenameColumn = useCallback(() => {
    if (!menuColumn || !apiRef.current) return;
    const colDef = apiRef.current.getColumn(menuColumn);
    const currentName = colDef?.headerName || menuColumn;
    setRenameField(menuColumn);
    setRenameValue(currentName);
    setRenameDialogOpen(true);
    handleCloseColumnMenu();
  }, [menuColumn, apiRef, handleCloseColumnMenu]);
  const handleRenameConfirm = useCallback(() => {
    if (renameField && renameValue.trim()) {
      setColumnNameOverrides((prev) => ({
        ...prev,
        [renameField]: renameValue.trim(),
      }));
    }
    setRenameDialogOpen(false);
    setRenameField(null);
    setRenameValue("");
  }, [renameField, renameValue, setColumnNameOverrides]);
  const handleRenameCancel = useCallback(() => {
    setRenameDialogOpen(false);
    setRenameField(null);
    setRenameValue("");
  }, []);

  const handleRenameRemove = useCallback(() => {
    if (renameField) {
      setColumnNameOverrides((prev) => ({
        ...prev,
        [renameField]: null,
      }));
      onRenameRemove?.(renameField);
    }
    setRenameDialogOpen(false);
    setRenameField(null);
    setRenameValue("");
  }, [renameField, setColumnNameOverrides, onRenameRemove]);

  return {
    columnMenuAnchor,
    menuColumn,
    renameDialogOpen,
    renameField,
    renameValue,
    columnMenuOpen,
    handleColumnHeaderClick,
    handleColumnHeaderKeyDown,
    handleColumnHeaderKeyUp,
    handleColumnHeaderContextMenu,
    handleCloseColumnMenu,
    handleSortAsc,
    handleSortDesc,
    handleClearSort,
    isMenuColumnSorted,
    handleRenameColumn,
    handleRenameConfirm,
    handleRenameCancel,
    handleRenameRemove,
    setRenameValue,
  };
};
